import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar, useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Attribute } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromAttribute, hasValues, toAttributeUpdate } from '../api/attribute-mappers';
import { useUpdateAttribute } from '../hooks/use-attribute-mutations';
import {
  attributeFormSchema,
  type AttributeFormInput,
  type AttributeFormValues,
} from '../schemas/attribute-form-schema';

import { AttributeDeleteDialog } from './attribute-delete-dialog';
import { AttributeFlagFields, AttributeUnitField } from './attribute-fields';
import { AttributeValuesEditor } from './attribute-values-editor';

const LIST_PATH = '/org/catalog-setup/attributes' as const;

function DangerZone({ attribute }: { attribute: Attribute }) {
  const { t } = useTranslation('attributes');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('danger.description')}</p>
      <div>
        <Button
          type="button"
          variant="outline"
          className="text-destructive hover:text-destructive"
          onClick={() => setOpen(true)}
        >
          <Trash2 aria-hidden />
          {t('danger.delete')}
        </Button>
      </div>
      <AttributeDeleteDialog
        attribute={open ? attribute : null}
        onOpenChange={setOpen}
        onDeleted={() => {
          // The attribute is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: LIST_PATH });
        }}
      />
    </FormSection>
  );
}

/**
 * One attribute's page inside the Attributes tab: settings under the save bar (key and type
 * read-only), then its values, then deletion. Re-key it on `updatedAt` so a save resets it.
 */
export function AttributeEditor({ attribute }: { attribute: Attribute }) {
  const { t } = useTranslation(['attributes', 'errors']);
  const { can } = usePermissions();
  const canWrite = can('attributes.write');
  const message = useErrorMessage();
  const update = useUpdateAttribute();
  const router = useRouter();
  const form = useForm<AttributeFormInput, unknown, AttributeFormValues>({
    resolver: useI18nResolver(attributeFormSchema),
    defaultValues: fromAttribute(attribute),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: attribute.id, body: toAttributeUpdate(values) });
      form.reset(values);
      // The crumb is loader data; a rename shows there only once the loader runs again.
      void router.invalidate();
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: !canWrite });

  const rootError = form.formState.errors.root?.server;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-muted-foreground -ml-2 h-7 w-fit px-2"
        >
          <Link to={LIST_PATH}>
            <ArrowLeft aria-hidden />
            {t('attributes:back')}
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-semibold tracking-tight">{attribute.label}</h2>
          <Badge variant="secondary" className="font-normal">
            {t(`attributes:dataType.${attribute.dataType}`)}
          </Badge>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={submit} noValidate>
          <FormSection
            title={t('attributes:edit.settings')}
            description={t('attributes:edit.settingsDescription')}
          >
            {rootError?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertTitle>{t('attributes:edit.saveFailed')}</AlertTitle>
                <AlertDescription>{rootError.message}</AlertDescription>
              </Alert>
            ) : null}
            <fieldset disabled={!canWrite} className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('attributes:fields.label')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('attributes:fields.key')}</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly disabled className="font-mono text-[13px]" />
                    </FormControl>
                    <FormDescription>{t('attributes:fields.keyFixed')}</FormDescription>
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${attribute.id}-type`}>{t('attributes:fields.dataType')}</Label>
                <Input
                  id={`${attribute.id}-type`}
                  value={t(`attributes:dataType.${attribute.dataType}`)}
                  readOnly
                  disabled
                />
                <p className="text-muted-foreground text-sm">
                  {t('attributes:fields.dataTypeFixed')}
                </p>
              </div>
              <AttributeUnitField dataType={attribute.dataType} />
              <div className="sm:col-span-2">
                <AttributeFlagFields dataType={attribute.dataType} />
              </div>
            </fieldset>
          </FormSection>
        </form>
      </Form>
      {hasValues(attribute.dataType) ? <AttributeValuesEditor attribute={attribute} /> : null}
      <Can permission="attributes.write">
        <DangerZone attribute={attribute} />
      </Can>
    </div>
  );
}
